<script lang="ts" setup>
  import type { Restaurant, TagIcon } from '@/types/api/restaurant'
  import { filterOrderTypeTags, getRestaurantTags } from '@/utils/restaurant'

  const props = defineProps<{
    restaurant: Restaurant
  }>()

  const distanceDisplay = computed<string>(() => {
    const distance = props.restaurant.distanceInMetres

    if (distance === null) {
      return ''
    }

    if (distance < 1000) {
      return `${distance.toFixed(0)}m`
    }

    return `${(distance / 1000).toFixed(1)}km`
  })

  /**
   * @description Combine order types and restaurant marketing tags to create a list of chips
   */
  const chips = computed<TagIcon[]>(() => {
    const marketingTags =
      props.restaurant.tags && Object.hasOwn(props.restaurant.tags, 'marketing')
        ? props.restaurant.tags.marketing
        : undefined
    const filteredOrderTypes = filterOrderTypeTags(props.restaurant.orderTypes)

    return getRestaurantTags(filteredOrderTypes, marketingTags)
  })
</script>

<template>
  <div class="c-restaurant-card">
    <div class="info">
      <div class="name-address-distance">
        <div class="name-address">
          <div class="name body-large">{{ restaurant.name }}</div>
          <div class="address">{{ restaurant.address }}</div>
        </div>
        <div class="distance body-large">{{ distanceDisplay }}</div>
      </div>

      <div v-if="chips.length" class="restaurant-chips">
        <CChip v-for="chip in chips" :key="chip.name" :icon="chip.icon" :text="chip.name" />
      </div>
    </div>

    <div class="restaurant-card-buttons">
      <CLink :href="`/restaurants/${restaurant.slug}`">
        <CButton variant="secondary" size="sm">Details</CButton>
      </CLink>

      <CLink :href="'home&restaurant=' + restaurant.id" is-embedded-app-link>
        <CButton variant="primary" size="sm">View Menu</CButton>
      </CLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .c-restaurant-card {
    background-color: var(--color-base-white);

    padding: 24px;
    border-radius: 8px;

    display: flex;
    flex-direction: column;
    gap: 16px;

    @include screenMdAndUp {
      flex-direction: row;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 16px;

    flex-grow: 1;

    .name-address-distance {
      display: flex;
      flex-direction: row;
      gap: 12px;

      .name-address {
        flex-grow: 1;

        display: flex;
        flex-direction: column;

        .address {
          @include typography-body-small;
          color: var(--color-text-muted);
          text-decoration: underline;
        }
      }

      .distance {
        margin-top: 10px;
      }
    }

    .restaurant-chips {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .restaurant-card-buttons {
    display: flex;
    flex-direction: row;
    gap: 12px;
    height: fit-content;

    :deep(.c-link) {
      flex: 1;

      button {
        width: 100%;
      }
    }
  }
</style>
